import { create } from '@bufbuild/protobuf'
import type { Transport } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation as useConnectMutation,
  useQuery as useConnectQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import type {
  Consents,
  GetConsentResponse,
} from '@jotta/grpc-connect-openapi/people'
import {
  Consents_ConsentState,
  Consents_FaceGroupingPrompt,
  Consents_FaceGroupingStatus,
  ConsentsSchema,
  GetConsentResponseSchema,
} from '@jotta/grpc-connect-openapi/people'
import {
  getConsent,
  updateConsent,
} from '@jotta/grpc-connect-openapi/peopleQuery'

function peopleConsentQueryKey(transport: Transport) {
  return createConnectQueryKey({
    schema: getConsent,
    input: {},
    transport,
    cardinality: 'finite',
  })
}

export type FaceGroupingGrant =
  | { kind: 'primaryPrompt' }
  | { kind: 'secondaryPrompt' }
  | { kind: 'locked'; untilMillis: number | undefined }
  | { kind: 'noPrompt' }

interface FaceGroupingConsentInfo {
  isFaceGroupsEnabled: boolean
  hasConsent: boolean
  canReadPeople: boolean
  grant: FaceGroupingGrant
}

// A missing grant or an unrecognised prompt kind means no prompt. The proto's
// "prompt while NOT_ANSWERED" fallback for an unset grant is deliberately not
// applied: the backend alone decides when to ask.
function selectFaceGroupingGrant(
  grant: Consents['faceGroupingGrant'],
): FaceGroupingGrant {
  switch (grant.case) {
    case 'prompt':
      switch (grant.value.kind) {
        case Consents_FaceGroupingPrompt.PRIMARY:
          return { kind: 'primaryPrompt' }
        case Consents_FaceGroupingPrompt.SECONDARY:
          return { kind: 'secondaryPrompt' }
        default:
          return { kind: 'noPrompt' }
      }
    case 'locked':
      return {
        kind: 'locked',
        untilMillis:
          grant.value.untilMillis === undefined
            ? undefined
            : Number(grant.value.untilMillis),
      }
    case 'noPrompt':
    case undefined:
      return { kind: 'noPrompt' }
  }
}

function selectFaceGroupingConsentInfo(
  response: GetConsentResponse,
): FaceGroupingConsentInfo {
  // proto3 gives message fields no presence, so consents is optional in the
  // generated type even though every response carries it. Decoding an absent
  // one to its zero values once keeps that out of the reads below.
  const consents = response.consents ?? create(ConsentsSchema, {})
  const hasConsent = consents.allowFaceGrouping === Consents_ConsentState.YES

  return {
    isFaceGroupsEnabled: response.faceGroupsEnabled,
    hasConsent,
    // Consent gates the content, and READY gates the people endpoints, which
    // answer 400 below it.
    canReadPeople:
      hasConsent &&
      consents.faceGroupingStatus === Consents_FaceGroupingStatus.READY,
    grant: selectFaceGroupingGrant(consents.faceGroupingGrant),
  }
}

/**
 * No staleTime: faceGroupingStatus moves on the server, on its own schedule,
 * and answering the consent is what sets it moving. Any window in which a
 * cached copy counts as fresh is a window serving a status we cannot vouch for,
 * so every mount asks again. Grouping runs long enough that the answer belongs
 * to the session that reads it, not the one that consented.
 *
 * A read landing right after an answer cannot undo it, because GetConsent is
 * read-your-writes consistent with UpdateConsent. Only the status can come back
 * different, which is the point.
 */
export function useFaceGroupingConsentInfoQuery() {
  const transport = useTransport()

  return useConnectQuery(
    getConsent,
    {},
    {
      transport,
      select: selectFaceGroupingConsentInfo,
    },
  )
}

export function useUpdateFaceGroupingConsent() {
  const transport = useTransport()
  const queryClient = useQueryClient()

  return useConnectMutation(updateConsent, {
    transport,
    // A failed answer must leave the prompt or toggle usable, not replace the
    // page with the error boundary. Callers report the failure.
    throwOnError: false,
    // A refetch already in flight resolves with the pre-mutation value and
    // would overwrite the cache write below, silently reverting the toggle.
    onMutate: () =>
      queryClient.cancelQueries({ queryKey: peopleConsentQueryKey(transport) }),
    // UpdateConsentResponse only carries the consents, so faceGroupsEnabled is
    // carried over from the cached GetConsentResponse the toggle was rendered
    // from.
    onSuccess: response => {
      queryClient.setQueryData<GetConsentResponse>(
        peopleConsentQueryKey(transport),
        previous =>
          create(GetConsentResponseSchema, {
            consents: response.consents,
            faceGroupsEnabled: previous?.faceGroupsEnabled,
          }),
      )
    },
  })
}
